import CrioTextField from '@crio/crio-react-component/dist/cjs/components/Inputs/CrioTextField';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { RecordQuestion } from '../../../types';
import { AnswerType } from '../../../enums';
import { getBeforeFirstParen } from '../../../util/stringUtil';

// Only format TimeInterval values for display, leave other calculations unchanged
export const getDisplayValue = (answer: any, calculationType: string, i18n: any) => {
  if (calculationType === 'TimeInterval' && answer && typeof answer === 'string') {
    const locale = i18n.language || 'en';
    const isEnglish = locale?.toLowerCase().startsWith('en') ?? true;

    try {
      // Parse the stored dd-MMM-yyyy HH:mm format
      const dateTime = DateTime.fromFormat(answer, 'dd-MMM-yyyy HH:mm');

      if (dateTime.isValid) {
        // Format according to language preference
        const displayFormat = isEnglish ? 'dd-MMM-yyyy HH:mm' : 'dd-MM-yyyy HH:mm';
        const formatted = dateTime.toFormat(displayFormat).toUpperCase();

        return formatted;
      }
    } catch (error) {
      console.error('Error parsing TimeInterval date:', error);
    }

    // Fallback to original value if parsing fails
    return answer;
  }
  // For all other calculation types (Age, BMI, etc.), return answer as-is
  return answer;
};

/* WARNING
  We definitely do not want to get anything from the context directly at the question-level.
  Context value, when updated, triggers a blind rerender of EVERYTHING that makes use of it
  via useContext. Instead, things like handleAnswerChange and the dataPoints nested inside
  records are provided as props, therefore dodging the expensive context rerenders.
*/
function CalculationQuestion(props: RecordQuestion) {
  const { t, i18n } = useTranslation();
  const {
    variableName, dataPoint, handleAnswerChange, recordId, questionId, calculation,
  } = props;
  const { answer } = dataPoint || {};
  const calculationType: string = getBeforeFirstParen(calculation?.calculationRule);
  if (dataPoint && calculationType === 'TimeInterval') {
    dataPoint.answer_type = AnswerType.TEXT;
  } else if (dataPoint) {
    dataPoint.answer_type = AnswerType.NUMBER;
  }

  const displayValue = getDisplayValue(answer, calculationType, i18n);

  return (
    // 0 is false, but a valid answer, so we can't just check !answer
    (answer !== 0 && !answer) ? <span className="CalculatedValue" data-testid={`answer-${variableName}-placeholder`}>{t('Procedure.Question.Calculation.Calculated Value')}</span>
      : (
        <CrioTextField
          type={dataPoint?.answer_type === AnswerType.TEXT ? 'text' : 'number'} // eslint-disable-line eqeqeq
          name={variableName}
          value={displayValue}
          disabled
          inputProps={{ 'data-testid': `answer-${variableName}` }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const newDataPoint = {
              ...dataPoint,
              answer: e.target.value,
            };

            handleAnswerChange({
              variableName,
              questionId,
              newDataPoint,
              recordId,
              runRules: newDataPoint?.answer !== dataPoint?.answer,
            });
          }}
        />
      )
  );
}

export default CalculationQuestion;
